import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LightBulbIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  BanknotesIcon,
  ChartBarIcon,
  StarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface SmartRecommendation {
  id: string;
  type: 'cost_saving' | 'revenue_boost' | 'efficiency' | 'risk_mitigation' | 'market_opportunity';
  priority: 'urgent' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  reasoning: string;
  impact: {
    financial: number;
    timeframe: string;
    confidence: number;
  };
  actionSteps: string[];
  requirements: {
    budget?: number;
    time: string;
    resources: string[];
  };
  status: 'new' | 'in_progress' | 'completed' | 'dismissed';
  aiScore: number;
  category: string;
  tags: string[];
}

interface SmartRecommendationsProps {
  entityId: string;
  entityType: string;
  maxRecommendations?: number;
}

const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({
  entityId,
  entityType,
  maxRecommendations = 10
}) => {
  const { t, direction } = useLanguage();
  const [recommendations, setRecommendations] = useState<SmartRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecommendation, setSelectedRecommendation] = useState<SmartRecommendation | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    // Simulate AI-generated recommendations
    const generateRecommendations = (): SmartRecommendation[] => {
      return [
        {
          id: '1',
          type: 'cost_saving',
          priority: 'high',
          title: 'تحسين كفاءة الإعلانات الرقمية',
          description: 'تحليل الذكاء الاصطناعي يشير إلى إمكانية توفير 35% من تكاليف الإعلانات',
          reasoning: 'بناءً على تحليل أداء الحملات الإعلانية خلال الـ6 أشهر الماضية، لوحظ أن 40% من الميزانية تُصرف على كلمات مفتاحية منخفضة الأداء. إعادة توزيع هذه الميزانية على الكلمات عالية الأداء ستحقق نتائج أفضل.',
          impact: {
            financial: 42000,
            timeframe: '30 يوم',
            confidence: 92
          },
          actionSteps: [
            'تحليل أداء الكلمات المفتاحية الحالية',
            'تحديد الكلمات منخفضة الأداء',
            'إعادة توزيع الميزانية على الكلمات عالية الأداء',
            'مراقبة النتائج وتحسينها'
          ],
          requirements: {
            time: '2-3 أسابيع',
            resources: ['متخصص تسويق رقمي', 'أدوات تحليل الإعلانات']
          },
          status: 'new',
          aiScore: 94,
          category: 'تسويق رقمي',
          tags: ['توفير', 'إعلانات', 'كفاءة']
        },
        {
          id: '2',
          type: 'revenue_boost',
          priority: 'urgent',
          title: 'استغلال موسم الذروة القادم',
          description: 'فرصة لزيادة الإيرادات بنسبة 45% خلال موسم الذروة',
          reasoning: 'التحليل التنبؤي يشير إلى نمو الطلب بنسبة 60% خلال الشهرين القادمين. زيادة المخزون وتحسين العمليات سيمكن من الاستفادة الكاملة من هذه الفرصة.',
          impact: {
            financial: 180000,
            timeframe: '60 يوم',
            confidence: 87
          },
          actionSteps: [
            'زيادة المخزون بنسبة 40%',
            'تعزيز فريق خدمة العملاء',
            'تحسين منصة التجارة الإلكترونية',
            'إطلاق حملة تسويقية مكثفة'
          ],
          requirements: {
            budget: 85000,
            time: '4-6 أسابيع',
            resources: ['فريق مبيعات إضافي', 'مخزون إضافي', 'حملة تسويقية']
          },
          status: 'new',
          aiScore: 89,
          category: 'مبيعات',
          tags: ['إيرادات', 'موسمي', 'فرصة']
        },
        {
          id: '3',
          type: 'efficiency',
          priority: 'medium',
          title: 'أتمتة العمليات المحاسبية',
          description: 'تحسين الكفاءة بنسبة 60% وتوفير 25 ساعة عمل أسبوعياً',
          reasoning: 'تحليل سير العمل يظهر أن 70% من المهام المحاسبية يمكن أتمتتها. هذا سيوفر وقت الموظفين للتركيز على المهام الاستراتيجية.',
          impact: {
            financial: 36000,
            timeframe: '90 يوم',
            confidence: 95
          },
          actionSteps: [
            'تقييم الأنظمة المحاسبية الحالية',
            'اختيار نظام محاسبي متطور',
            'تدريب الفريق على النظام الجديد',
            'تطبيق الأتمتة تدريجياً'
          ],
          requirements: {
            budget: 45000,
            time: '8-12 أسبوع',
            resources: ['نظام محاسبي', 'تدريب الموظفين', 'استشاري تقني']
          },
          status: 'new',
          aiScore: 91,
          category: 'محاسبة',
          tags: ['أتمتة', 'كفاءة', 'توفير وقت']
        }
      ];
    };

    setTimeout(() => {
      setRecommendations(generateRecommendations());
      setLoading(false);
    }, 1000);
  }, [entityId, entityType]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cost_saving': return BanknotesIcon;
      case 'revenue_boost': return ArrowTrendingUpIcon;
      case 'efficiency': return ChartBarIcon;
      case 'risk_mitigation': return ExclamationTriangleIcon;
      case 'market_opportunity': return FireIcon;
      default: return LightBulbIcon;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'cost_saving': return 'from-green-500 to-green-600';
      case 'revenue_boost': return 'from-blue-500 to-blue-600';
      case 'efficiency': return 'from-purple-500 to-purple-600';
      case 'risk_mitigation': return 'from-red-500 to-red-600';
      case 'market_opportunity': return 'from-orange-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleAcceptRecommendation = (id: string) => {
    setRecommendations(prev => 
      prev.map(rec => 
        rec.id === id ? { ...rec, status: 'in_progress' } : rec
      )
    );
  };

  const handleDismissRecommendation = (id: string) => {
    setRecommendations(prev => 
      prev.map(rec => 
        rec.id === id ? { ...rec, status: 'dismissed' } : rec
      )
    );
  };

  const filteredRecommendations = recommendations.filter(rec => {
    if (filterType === 'all') return rec.status !== 'dismissed';
    return rec.type === filterType && rec.status !== 'dismissed';
  });

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg">
              <LightBulbIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                التوصيات الذكية
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                مدعومة بالذكاء الاصطناعي
              </p>
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">جميع التوصيات</option>
            <option value="cost_saving">توفير التكاليف</option>
            <option value="revenue_boost">زيادة الإيرادات</option>
            <option value="efficiency">تحسين الكفاءة</option>
            <option value="risk_mitigation">تخفيف المخاطر</option>
            <option value="market_opportunity">فرص السوق</option>
          </select>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="p-6 space-y-4">
        {filteredRecommendations.slice(0, maxRecommendations).map((recommendation, index) => {
          const IconComponent = getTypeIcon(recommendation.type);
          
          return (
            <motion.div
              key={recommendation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-start space-x-4 rtl:space-x-reverse">
                {/* Icon */}
                <div className={`p-3 bg-gradient-to-r ${getTypeColor(recommendation.type)} rounded-xl flex-shrink-0`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {recommendation.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {recommendation.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2 rtl:space-x-reverse ml-4 rtl:ml-0 rtl:mr-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(recommendation.priority)}`}>
                        {recommendation.priority === 'urgent' ? 'عاجل' :
                         recommendation.priority === 'high' ? 'عالي' :
                         recommendation.priority === 'medium' ? 'متوسط' : 'منخفض'}
                      </span>
                      <div className="flex items-center space-x-1 rtl:space-x-reverse">
                        <StarIcon className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {recommendation.aiScore}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Impact Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">التأثير المالي</div>
                      <div className="font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(recommendation.impact.financial)}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">الإطار الزمني</div>
                      <div className="font-bold text-blue-600 dark:text-blue-400">
                        {recommendation.impact.timeframe}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">مستوى الثقة</div>
                      <div className="font-bold text-purple-600 dark:text-purple-400">
                        {recommendation.impact.confidence}%
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      {recommendation.tags.map((tag, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <button
                        onClick={() => setSelectedRecommendation(recommendation)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        عرض التفاصيل
                      </button>
                      <button
                        onClick={() => handleDismissRecommendation(recommendation.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAcceptRecommendation(recommendation.id)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      >
                        تطبيق
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Detailed View Modal */}
      <AnimatePresence>
        {selectedRecommendation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedRecommendation(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedRecommendation.title}
                  </h2>
                  <button
                    onClick={() => setSelectedRecommendation(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Reasoning */}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">التبرير:</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedRecommendation.reasoning}
                    </p>
                  </div>

                  {/* Action Steps */}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">خطوات التنفيذ:</h3>
                    <ol className="space-y-2">
                      {selectedRecommendation.actionSteps.map((step, idx) => (
                        <li key={idx} className="flex items-start space-x-3 rtl:space-x-reverse">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-sm rounded-full flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Requirements */}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">المتطلبات:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">الوقت المطلوب</div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {selectedRecommendation.requirements.time}
                        </div>
                      </div>
                      {selectedRecommendation.requirements.budget && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">الميزانية المطلوبة</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(selectedRecommendation.requirements.budget)}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">الموارد المطلوبة:</div>
                      <ul className="space-y-1">
                        {selectedRecommendation.requirements.resources.map((resource, idx) => (
                          <li key={idx} className="flex items-center space-x-2 rtl:space-x-reverse">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span className="text-gray-600 dark:text-gray-400">{resource}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setSelectedRecommendation(null)}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      إغلاق
                    </button>
                    <button
                      onClick={() => {
                        handleAcceptRecommendation(selectedRecommendation.id);
                        setSelectedRecommendation(null);
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      تطبيق التوصية
                      <ArrowRightIcon className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SmartRecommendations;
